// UltraPlanComparison.js
// Ultra Admin > Paket Karşılaştırma Matrisi editörü.
//
// Kullanıcı akışı:
//   • Sol sütun: karşılaştırma satırları (özellik adları). Ekle/sil/yeniden adlandır.
//   • Sağ sütunlar: her aktif/pasif paket için bir sütun.
//   • Her hücrede bir checkbox — işaretliyse /pricing tablosunda ✓, değilse ✗ gözükür.
//   • Kaydet butonu tüm matriksi tek atomik PUT ile backend'e yollar.
//   • BroadcastChannel üzerinden Landing/Pricing sekmelerine canlı yenileme sinyali gider.

import React, { useEffect, useState, useMemo } from 'react';
import { toast } from 'sonner';
import { PlusIcon, XIcon, SaveIcon, RefreshCwIcon, CheckIcon } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => {
  const ultra = localStorage.getItem('ultra_admin_token');
  const token = localStorage.getItem('access_token') || localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(ultra ? { Authorization: `Bearer ${ultra}` } : token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const UltraPlanComparison = () => {
  const [rows, setRows] = useState([]);       // [{label, category}, ...]
  const [plans, setPlans] = useState([]);     // [{slug, title, comparison_features:[...]}, ...]
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [suggestedCategories, setSuggestedCategories] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/ultra-admin/plan-comparison`, {
        headers: authHeaders(),
        cache: 'no-store',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      // Backend returns rows as [{label, category}]; normalize just in case
      // an older payload sneaks in as raw strings.
      const normalized = (data.rows || []).map(r =>
        typeof r === 'string' ? { label: r, category: '' } : { label: r.label || '', category: r.category || '' }
      );
      setRows(normalized);
      setPlans(data.plans || []);
      setSuggestedCategories(
        [...new Set(normalized.map(r => r.category).filter(Boolean))]
      );
      setDirty(false);
    } catch (e) {
      toast.error(`Yüklenemedi: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const rowEnabledForPlan = useMemo(() => {
    // Map<slug, Set<row label>> for O(1) checkbox lookups during render.
    const m = {};
    for (const p of plans) m[p.slug] = new Set(p.comparison_features || []);
    return m;
  }, [plans]);

  const isChecked = (planSlug, rowLabel) =>
    (rowEnabledForPlan[planSlug] || new Set()).has(rowLabel);

  const toggleCell = (planSlug, rowLabel) => {
    setPlans(prev => prev.map(p => {
      if (p.slug !== planSlug) return p;
      const set = new Set(p.comparison_features || []);
      if (set.has(rowLabel)) set.delete(rowLabel); else set.add(rowLabel);
      return { ...p, comparison_features: Array.from(set) };
    }));
    setDirty(true);
  };

  const addRow = () => {
    // Yeni satırı en son kullanılan kategoriye ekle — kullanıcı akışı doğal olsun.
    const lastCat = rows.length > 0 ? rows[rows.length - 1].category : '';
    setRows(prev => [...prev, { label: '', category: lastCat }]);
    setDirty(true);
    setTimeout(() => {
      const wrap = document.querySelector('[data-testid="matrix-scroll"]');
      if (wrap) wrap.scrollTop = wrap.scrollHeight;
    }, 50);
  };

  const editRowLabel = (idx, value) => {
    setRows(prev => {
      const oldLabel = prev[idx].label;
      const next = [...prev];
      next[idx] = { ...next[idx], label: value };
      // Rename in every plan's comparison_features so cell selections follow.
      if (oldLabel && oldLabel !== value) {
        setPlans(pprev => pprev.map(p => {
          if (!(p.comparison_features || []).includes(oldLabel)) return p;
          return {
            ...p,
            comparison_features: p.comparison_features.map(f => (f === oldLabel ? value : f)),
          };
        }));
      }
      return next;
    });
    setDirty(true);
  };

  const editRowCategory = (idx, value) => {
    setRows(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], category: value };
      return next;
    });
    setDirty(true);
  };

  const deleteRow = (idx) => {
    const target = rows[idx]?.label;
    if (!window.confirm(`"${target || '(boş satır)'}" karşılaştırma satırını silmek istiyor musunuz?`)) return;
    setRows(prev => prev.filter((_, i) => i !== idx));
    setPlans(prev => prev.map(p => ({
      ...p,
      comparison_features: (p.comparison_features || []).filter(f => f !== target),
    })));
    setDirty(true);
  };

  const toggleColumnAll = (slug, enable) => {
    setPlans(prev => prev.map(p => {
      if (p.slug !== slug) return p;
      return { ...p, comparison_features: enable ? rows.map(r => r.label).filter(Boolean) : [] };
    }));
    setDirty(true);
  };

  const save = async () => {
    // Guard: reject empty labels to keep the matrix clean.
    const cleaned = rows.map(r => ({ label: (r.label || '').trim(), category: (r.category || '').trim() }));
    if (cleaned.some(r => !r.label)) {
      toast.error('Boş etiketli satır var. Lütfen tamamlayın veya silin.');
      return;
    }
    const labels = cleaned.map(r => r.label);
    if (new Set(labels).size !== labels.length) {
      toast.error('Aynı ada sahip birden fazla satır var. Lütfen düzeltin.');
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/ultra-admin/plan-comparison`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({
          rows: cleaned,
          plans: plans.map(p => ({
            slug: p.slug,
            comparison_features: (p.comparison_features || []).filter(f => labels.includes(f)),
          })),
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      toast.success('Karşılaştırma matrisi kaydedildi ✓');
      setDirty(false);
      try {
        if ('BroadcastChannel' in window) {
          const bc = new BroadcastChannel('plans-updated');
          bc.postMessage('updated');
          bc.close();
        }
      } catch { /* ignore */ }
    } catch (e) {
      toast.error(`Kaydedilemedi: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-3">
          <div className="h-8 w-64 bg-gray-200 rounded"></div>
          <div className="h-64 bg-gray-100 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="ultra-plan-comparison">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Paket Karşılaştırma Matrisi</h1>
          <p className="text-sm text-gray-600 mt-1">
            Sol sütunda karşılaştırma satırlarını, sağdaki her paket sütununda checkbox&apos;ları düzenleyin.
            İşaretli hücreler <span className="text-green-600 font-medium">✓</span>, işaretsizler
            <span className="text-gray-400 font-medium ml-1">×</span> olarak <code className="bg-gray-100 px-1 rounded">/pricing</code> sayfasında gözükür.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={load} disabled={saving} data-testid="matrix-reload-btn">
            <RefreshCwIcon className="w-4 h-4 mr-2" /> Yenile
          </Button>
          <Button onClick={save} disabled={saving || !dirty} data-testid="matrix-save-btn"
                  className="bg-teal-600 hover:bg-teal-700 text-white">
            <SaveIcon className="w-4 h-4 mr-2" />
            {saving ? 'Kaydediliyor…' : dirty ? 'Değişiklikleri Kaydet' : 'Kaydedildi'}
          </Button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between gap-4">
        <div className="text-sm text-gray-500">
          {rows.length} satır × {plans.length} paket · {plans.reduce((a, p) => a + (p.comparison_features || []).length, 0)} işaretli hücre
        </div>
        <Button onClick={addRow} variant="outline" size="sm" data-testid="matrix-add-row-btn">
          <PlusIcon className="w-4 h-4 mr-1" /> Yeni Satır
        </Button>
      </div>

      {/* Matrix table */}
      <div className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm">
        <div className="overflow-x-auto max-h-[70vh]" data-testid="matrix-scroll">
          <table className="w-full min-w-[900px] text-sm">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th className="text-left px-4 py-3 font-semibold text-gray-700 sticky left-0 bg-gray-50 z-20 w-80 border-r border-gray-200">
                  Özellik
                </th>
                {plans.map(p => (
                  <th key={p.slug} className="px-4 py-3 text-center font-semibold text-gray-700 whitespace-nowrap min-w-[160px]">
                    <div>{p.title}</div>
                    <div className="text-xs text-gray-500 font-normal mt-0.5">{p.slug}</div>
                    <div className="flex items-center justify-center gap-1 mt-1">
                      <button
                        type="button"
                        onClick={() => toggleColumnAll(p.slug, true)}
                        className="text-[11px] text-teal-600 hover:underline"
                        data-testid={`matrix-col-all-${p.slug}`}
                      >
                        Tümü ✓
                      </button>
                      <span className="text-gray-300">|</span>
                      <button
                        type="button"
                        onClick={() => toggleColumnAll(p.slug, false)}
                        className="text-[11px] text-gray-500 hover:underline"
                        data-testid={`matrix-col-none-${p.slug}`}
                      >
                        Tümü ×
                      </button>
                    </div>
                  </th>
                ))}
                <th className="w-12 px-2 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={plans.length + 2} className="text-center py-10 text-gray-400 italic">
                    Henüz satır yok. Yukarıdan &quot;Yeni Satır&quot; ile başlayın.
                  </td>
                </tr>
              )}
              {rows.map((row, idx) => {
                const isFirstOfCategory = idx === 0 || rows[idx - 1].category !== row.category;
                return (
                  <React.Fragment key={idx}>
                    {isFirstOfCategory && row.category && (
                      <tr className="bg-gray-100" data-testid={`matrix-cat-header-${idx}`}>
                        <td colSpan={plans.length + 2}
                            className="px-4 py-2 text-xs font-bold uppercase tracking-wider text-gray-600 sticky left-0 bg-gray-100 z-10">
                          {row.category}
                        </td>
                      </tr>
                    )}
                    <tr className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'} data-testid={`matrix-row-${idx}`}>
                      <td className="px-3 py-2 sticky left-0 bg-inherit z-10 border-r border-gray-100">
                        <div className="flex flex-col gap-1">
                          <Input
                            value={row.label}
                            onChange={(e) => editRowLabel(idx, e.target.value)}
                            placeholder={`Özellik ${idx + 1}`}
                            className="h-8 text-sm"
                            data-testid={`matrix-row-input-${idx}`}
                          />
                          <Input
                            value={row.category}
                            onChange={(e) => editRowCategory(idx, e.target.value)}
                            placeholder="Kategori (opsiyonel) — örn. Avans, Raporlama"
                            list={`matrix-categories-list`}
                            className="h-7 text-xs text-gray-500 bg-gray-50/50"
                            data-testid={`matrix-row-category-${idx}`}
                          />
                        </div>
                      </td>
                      {plans.map(p => {
                        const checked = isChecked(p.slug, row.label);
                        return (
                          <td key={p.slug} className="text-center px-4 py-2">
                            <label className="inline-flex items-center justify-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleCell(p.slug, row.label)}
                                className="sr-only"
                                data-testid={`matrix-cell-${p.slug}-${idx}`}
                              />
                              <span className={`w-7 h-7 rounded flex items-center justify-center transition-all ${
                                checked
                                  ? 'bg-emerald-500 text-white shadow-sm hover:bg-emerald-600'
                                  : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                              }`}>
                                {checked ? <CheckIcon className="w-4 h-4" /> : <XIcon className="w-4 h-4" />}
                              </span>
                            </label>
                          </td>
                        );
                      })}
                      <td className="text-center px-2 py-2">
                        <button
                          type="button"
                          onClick={() => deleteRow(idx)}
                          className="w-7 h-7 rounded flex items-center justify-center text-red-500 hover:bg-red-50"
                          title="Satırı sil"
                          data-testid={`matrix-row-delete-${idx}`}
                        >
                          <XIcon className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })}
              {/* Datalist so category input can autocomplete previously-used labels. */}
            </tbody>
          </table>
          <datalist id="matrix-categories-list">
            {suggestedCategories.map(c => <option key={c} value={c} />)}
          </datalist>
        </div>
      </div>

      {dirty && (
        <div className="fixed bottom-6 right-6 bg-amber-50 border border-amber-200 text-amber-900 rounded-lg shadow-lg px-4 py-3 text-sm z-50">
          Kaydedilmemiş değişiklikler var — sağ üstteki <b>&quot;Değişiklikleri Kaydet&quot;</b> butonuna tıklayın.
        </div>
      )}
    </div>
  );
};

export default UltraPlanComparison;
